import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { showToast } from '@/components/ui/sonner';
import { CustomTextarea } from '@/components/ui/textarea';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { handleError } from '@/lib/utils';
import { getRoutes } from '@/routes/routePaths';
import { useMutation, useQuery } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { X } from 'lucide-react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';
import { GET_SESSION_BY_ID } from '../sessionDetails/GraphQl/queries';
import { UPDATE_SESSION } from '../sessions/GraphQl/mutation';

const helpUsImproveSchema = z.object({
  technicalIssues: z.string().min(1, 'Please select an option'),
  technicalIssuesSpecify: z.string().optional(),
  videoAudioQuality: z.string().min(1, 'Please rate the video and audio quality'),
  schedulingConvenience: z.string().min(1, 'Please rate the scheduling convenience'),
  continueLikelihood: z.string().min(1, 'Please select how likely you are to continue'),
  appUserFriendliness: z.string().min(1, 'Please rate the app user-friendliness'),
  sessionLikes: z.string().optional(),
  improvementSuggestions: z.string().optional(),
});

type HelpUsImproveFormData = z.infer<typeof helpUsImproveSchema>;

const style = {
  background:
    'linear-gradient(154.36deg, rgba(47, 47, 47, 0.44) 11.3%, rgba(47, 47, 47, 0.44) 95.38%)',
};

const HelpUsImprove = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data: sessionData } = useQuery(GET_SESSION_BY_ID as any, {
    variables: { id: id || '' },
  });
  const {
    control,
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<HelpUsImproveFormData>({
    resolver: zodResolver(helpUsImproveSchema),
    defaultValues: {
      appUserFriendliness: '',
      continueLikelihood: '',
      improvementSuggestions: '',
      schedulingConvenience: '',
      sessionLikes: '',
      technicalIssues: '',
      technicalIssuesSpecify: '',
      videoAudioQuality: '',
    },
  });

  const [updateSession, { loading }] = useMutation(UPDATE_SESSION, {
    onCompleted: () => {
      showToast({ message: 'Thank you for your feedback', variant: 'success' });
      redirectToHome();
    },
    onError: handleError,
    refetchQueries: ['GetAllSession', 'GetSessionById'],
  });

  const onSubmit = (data: HelpUsImproveFormData) => {
    if (id && !loading) {
      updateSession({
        variables: {
          data: {
            _id: id,
            programId: sessionData?.getSessionById?.programId || '',
            technicalIssuesEncountered: data.technicalIssues === 'Yes' ? true : false,
            technicalIssuesDescription: data.technicalIssuesSpecify,
            videoAudioQuality: data.videoAudioQuality,
            schedulingConvenience: data.schedulingConvenience,
            continuationLikelihood: data.continueLikelihood,
            appUserFriendliness: data.appUserFriendliness,
            sessionLikes: data.sessionLikes,
            futureSessionImprovement: data.improvementSuggestions,
          },
        },
      });
    }
  };

  const redirectToHome = () => {
    navigate(getRoutes.Sessions, { replace: true });
  };

  return (
    <div className='w-full flex justify-center items-center'>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className='flex flex-col gap-5 w-[761px] max-w-[95%] px-3 py-5 md:p-8 bg-[#151525] mt-16 mb-16 rounded-xl'
      >
        <div className='w-full flex justify-between items-center'>
          <h2>Help us improve</h2>
          <Button id='feedback-close-btn' size='icon' variant='ghost' onClick={redirectToHome}>
            <X className='w-6 h-6' />
          </Button>
        </div>
        <div className='flex flex-col gap-4 bg-bgPrimary rounded-xl p-4' style={style}>
          <Label className='text-base font-semibold'>
            Did you encounter any technical issues while using the app?
          </Label>
          <Controller
            name='technicalIssues'
            control={control}
            render={({ field }) => (
              <ToggleGroup
                type='single'
                className='grid grid-cols-4 gap-3'
                onValueChange={field.onChange}
                value={field.value}
              >
                <ToggleGroupItem value='No'>No</ToggleGroupItem>
                <ToggleGroupItem value='Yes'>Yes</ToggleGroupItem>
              </ToggleGroup>
            )}
          />
          {errors.technicalIssues && (
            <p className='text-red text-xs'>{errors.technicalIssues.message}</p>
          )}
          <CustomTextarea
            id='improve-text-0'
            label='Please Specify'
            {...register('technicalIssuesSpecify')}
            style={style}
            labelStyle={{ background: '#27272c' }}
          />
        </div>
        <div className='flex flex-col gap-4 rounded-xl p-4' style={style}>
          <Label className='text-base font-semibold'>Rate the video and audio quality:</Label>
          <Controller
            name='videoAudioQuality'
            control={control}
            render={({ field }) => (
              <ToggleGroup
                type='single'
                className='grid grid-cols-4 gap-3'
                onValueChange={field.onChange}
                value={field.value}
              >
                <ToggleGroupItem value='Excellent'>Excellent</ToggleGroupItem>
                <ToggleGroupItem value='Good'>Good</ToggleGroupItem>
                <ToggleGroupItem value='Average'>Average</ToggleGroupItem>
                <ToggleGroupItem value='BelowAverage'>Below Average</ToggleGroupItem>
              </ToggleGroup>
            )}
          />
          {errors.videoAudioQuality && (
            <p className='text-red text-xs'>{errors.videoAudioQuality.message}</p>
          )}
        </div>
        <div className='flex flex-col gap-4 bg-bgPrimary rounded-xl p-4' style={style}>
          <Label className='text-base font-semibold'>How convenient was scheduling?</Label>
          <Controller
            name='schedulingConvenience'
            control={control}
            render={({ field }) => (
              <ToggleGroup
                type='single'
                className='grid grid-cols-4 gap-3'
                onValueChange={field.onChange}
                value={field.value}
              >
                <ToggleGroupItem value='Very Convenient'>Very Convenient</ToggleGroupItem>
                <ToggleGroupItem value='Convenient'>Convenient</ToggleGroupItem>
                <ToggleGroupItem value='Inconvenient'>Inconvenient</ToggleGroupItem>
              </ToggleGroup>
            )}
          />
          {errors.schedulingConvenience && (
            <p className='text-red text-xs'>{errors.schedulingConvenience.message}</p>
          )}
        </div>
        <div className='flex flex-col gap-4 bg-bgPrimary rounded-xl p-4' style={style}>
          <Label className='text-base font-semibold'>
            How likely are you to continue with FitStatic sessions?
          </Label>
          <Controller
            name='continueLikelihood'
            control={control}
            render={({ field }) => (
              <ToggleGroup
                type='single'
                className='grid grid-cols-4 gap-3'
                onValueChange={field.onChange}
                value={field.value}
              >
                <ToggleGroupItem value='VeryLikely'>Very Likely</ToggleGroupItem>
                <ToggleGroupItem value='Likely'>Likely</ToggleGroupItem>
                <ToggleGroupItem value='Unsure'>Unsure</ToggleGroupItem>
                <ToggleGroupItem value='Unlikely'>Unlikely</ToggleGroupItem>
              </ToggleGroup>
            )}
          />
          {errors.continueLikelihood && (
            <p className='text-red text-xs'>{errors.continueLikelihood.message}</p>
          )}
        </div>
        <div className='flex flex-col gap-4 bg-bgPrimary rounded-xl p-4' style={style}>
          <Label className='text-base font-semibold'>How user-friendly did you find the app?</Label>
          <Controller
            name='appUserFriendliness'
            control={control}
            render={({ field }) => (
              <ToggleGroup
                type='single'
                className='grid grid-cols-4 gap-3'
                onValueChange={field.onChange}
                value={field.value}
              >
                <ToggleGroupItem value='Excellent'>Excellent</ToggleGroupItem>
                <ToggleGroupItem value='Good'>Good</ToggleGroupItem>
                <ToggleGroupItem value='Average'>Average</ToggleGroupItem>
                <ToggleGroupItem value='BelowAverage'>Below Average</ToggleGroupItem>
              </ToggleGroup>
            )}
          />
          {errors.appUserFriendliness && (
            <p className='text-red text-xs'>{errors.appUserFriendliness.message}</p>
          )}
        </div>
        <div className='flex flex-col gap-4 bg-bgPrimary rounded-xl p-4' style={style}>
          <Label className='text-base font-semibold'>
            What did you like most about the session?
          </Label>
          <CustomTextarea
            id='improve-text-1'
            label='Answer'
            style={style}
            labelStyle={{ background: '#27272c' }}
            {...register('sessionLikes')}
          />
        </div>
        <div className='flex flex-col gap-4 bg-bgPrimary rounded-xl p-4' style={style}>
          <Label className='text-base font-semibold'>
            What can we do to improve future sessions?
          </Label>
          <CustomTextarea
            id='improve-text-2'
            label='Answer'
            style={style}
            labelStyle={{ background: '#27272c' }}
            {...register('improvementSuggestions')}
          />
        </div>
        <div className='flex gap-4'>
          <Button className='w-full' id='improve-skip' variant='secondary' onClick={redirectToHome}>
            Skip
          </Button>
          <Button type='submit' loading={loading} className='w-full' id='improve-continue'>
            Continue
          </Button>
        </div>
      </form>
    </div>
  );
};

export default HelpUsImprove;
